import { Crown, X } from '@phosphor-icons/react';
import type { Worker } from '@mybusinessmate/schema';
import type { TeamPick } from '../../lib/task-form';
import { initialsOf } from '../../lib/initials';
import { Avatar } from '../ui/avatar';
import { Card } from '../ui/card';
import { IconButton } from '../ui/icon-button';
import { Icon } from '../Icon';
import { PeoplePicker, type AddResult } from './team-panel';

const line = (w: TeamPick) =>
  [
    w.staffRole === 'lead_specialist' ? 'Lead' : w.title,
    w.staffRole === 'lead_specialist' ? w.department : null,
    `${w.activeTaskCount} of ${w.maxActiveTasks} tasks`,
  ]
    .filter(Boolean)
    .join(' · ');

/**
 * Figma A05.3 "Team": who the task starts with. Nothing is saved here; the choice goes up with the task when it is
 * created, and putting someone on moves an intake task to "In Progress". Someone at their task limit asks for a second yes.
 */
export function NewTaskTeam({
  team,
  onChange,
  onConfirmOverCapacity,
}: {
  team: TeamPick[];
  onChange: (team: TeamPick[]) => void;
  /** Called when the person confirmed adding someone at capacity: the request must then carry the flag. */
  onConfirmOverCapacity: () => void;
}) {
  const onAdd = (people: Worker[], confirmOverCapacity: boolean): Promise<AddResult> => {
    const atCapacity = confirmOverCapacity ? [] : people.filter((w) => w.activeTaskCount >= w.maxActiveTasks);
    const ok = people.filter((w) => !atCapacity.includes(w));
    if (confirmOverCapacity && people.some((w) => w.activeTaskCount >= w.maxActiveTasks)) onConfirmOverCapacity();
    if (ok.length > 0) onChange([...team, ...ok]);
    return Promise.resolve({ added: ok.map((w) => w.id), atCapacity });
  };

  return (
    <Card className="flex flex-col gap-(--mbm-space-4)">
      <div className="flex items-center justify-between gap-(--mbm-space-3)">
        <h2 className="text-heading-h3 text-text-primary">Team</h2>
        <PeoplePicker team={team} onAdd={onAdd} />
      </div>
      {team.length === 0 ? (
        <p className="text-body-small text-text-secondary">
          Nobody yet. It goes to Task intake for someone to pick up.
        </p>
      ) : (
        <ul className="flex flex-col gap-(--mbm-space-3)">
          {team.map((m) => (
            <li key={m.id} className="flex items-center justify-between gap-(--mbm-space-3)">
              <span className="flex items-center gap-(--mbm-space-3)">
                <Avatar tone="neutral" size="md" initials={initialsOf(m.name)} />
                <span className="flex min-w-0 flex-col">
                  <span className="flex items-center gap-(--mbm-space-1-5) text-label-large text-text-primary">
                    {m.name}
                    {m.staffRole === 'lead_specialist' ? <Icon icon={Crown} size="sm" /> : null}
                  </span>
                  <span className="text-body-small text-text-secondary">{line(m)}</span>
                </span>
              </span>
              <IconButton
                icon={X}
                variant="ghost"
                iconSize="sm"
                aria-label={`Remove ${m.name}`}
                onClick={() => onChange(team.filter((t) => t.id !== m.id))}
              />
            </li>
          ))}
        </ul>
      )}
      <p className="text-body-xsmall text-text-tertiary">
        Leads are listed first and handle QA. People a lead adds wait for admin approval; an admin’s or account
        manager’s apply right away.
      </p>
    </Card>
  );
}
